import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Switch, Empty } from 'antd';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import NewCustomButton, { ButtonTypes } from '../Common/NewCustomButton';
import { NewCloseIcon, NewUpdateIcon } from '../Common/Icons';
import useAppRegistryApps from '../Hooks/useAppRegistryApps';
import useBulkUpdateAppRegistryOverrides from '../Hooks/useBulkUpdateAppRegistryOverrides';
import useIsDarkTheme from '../Hooks/useIsDarkTheme';
import { getChannelIconUrl } from '../Common/Icons/ChannelIcons';
import { HSCard, HSCardSkeleton } from '@/Components/Common/HSWidget';

// Shared card list for the Channels/Apps tabs, split by apps[].metadata.launcher_placement.
// Toggling stages a local change (no API call); Update sends every staged change in one
// bulk PUT, Cancel discards them. `ref` exposes hasPendingChanges/save/discard so the parent
// (EditUserProfile) can guard tab switches while there are unsaved toggle changes.
const AppRegistryList = forwardRef(({ useruuid, launcherPlacement, canManageApps }, ref) => {
  const trans = useSelector(translator);
  const { data: apps = [], isLoading } = useAppRegistryApps({ useruuid });
  const bulkUpdateOverrides = useBulkUpdateAppRegistryOverrides({ useruuid });
  const isDarkTheme = useIsDarkTheme();
  const [pendingChanges, setPendingChanges] = useState({});
  // Tracks (appuuid + icon URL) pairs whose API-provided icon URL failed to load in the browser.
  // <img> onError doesn't expose the actual HTTP status, so any load failure on an API-sourced
  // URL is treated the same way: drop to the bundled channel SVG instead of just hiding the icon.
  // Keying by URL (not just appuuid) means a later refetch/theme change with a new URL isn't
  // permanently bypassed by a stale failure.
  const [failedApiIconKeys, setFailedApiIconKeys] = useState({});

  const filteredApps = useMemo(
    () =>
      apps
        .filter((app) => app?.metadata?.launcher_placement?.toLowerCase() === launcherPlacement)
        .sort((a, b) =>
          (a?.metadata?.name ?? a?.name ?? '').localeCompare(
            b?.metadata?.name ?? b?.name ?? '',
            undefined,
            {
              sensitivity: 'base',
            }
          )
        ),
    [apps, launcherPlacement]
  );

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const handleEnableChange = (app, checked) => {
    setPendingChanges((prev) => {
      const next = { ...prev };
      if (checked === !!app.enabled) {
        delete next[app.appuuid];
      } else {
        next[app.appuuid] = checked;
      }
      return next;
    });
  };

  const discardPendingChanges = () => setPendingChanges({});

  const savePendingChanges = () =>
    new Promise((resolve, reject) => {
      if (!hasPendingChanges) {
        resolve();
        return;
      }
      const items = Object.entries(pendingChanges).map(([appuuid, enabled]) => ({
        appuuid,
        enabled,
      }));
      bulkUpdateOverrides.mutate({
        items,
        successCallback: () => {
          setPendingChanges({});
          resolve();
        },
        errorCallback: (err) => reject(err),
      });
    });

  useImperativeHandle(ref, () => ({
    hasPendingChanges: () => hasPendingChanges,
    save: savePendingChanges,
    discard: discardPendingChanges,
  }));

  if (isLoading) {
    return <HSCardSkeleton />;
  }

  return (
    <div className="app-registry-content-container-newui">
      {filteredApps.length === 0 ? (
        <Empty description={trans('NODATAFOUND')} />
      ) : (
        filteredApps.map((app) => {
          const label = app?.metadata?.name ?? app?.name ?? '';
          // Prefer the icon matching the active theme. If only the other variant is provided,
          // use it; when neither API variant is available (or the URL fails), use the bundled SVG.
          const apiIconUrl = isDarkTheme
            ? app?.metadata?.dark_launcher_icon || app?.metadata?.light_launcher_icon
            : app?.metadata?.light_launcher_icon || app?.metadata?.dark_launcher_icon;
          const bundledSvgUrl = getChannelIconUrl(
            app?.metadata?.name,
            app?.metadata?.launcher_label
          );
          // If this exact app + icon URL already failed to load once, don't retry it - go
          // straight to the bundled SVG (or nothing, if there isn't one).
          const apiIconKey = `${app.appuuid}:${apiIconUrl}`;
          const apiIconHasFailed = !!failedApiIconKeys[apiIconKey];
          const iconUrl = (!apiIconHasFailed && apiIconUrl) || bundledSvgUrl;

          const enabled = pendingChanges[app.appuuid] ?? !!app.enabled;

          return (
            <HSCard
              key={app.appuuid}
              className="app-registry-card-newui"
              content={
                <>
                  <span className="app-registry-card-icon-newui">
                    {/* Packaged icon fallback for apps without a theme-specific launcher icon */}
                    {iconUrl && (
                      <img
                        src={iconUrl}
                        alt={label}
                        onError={(event) => {
                          // Currently showing an API-sourced URL that just failed to load (e.g. a
                          // 403 from an expired signed URL) and there's a bundled SVG to fall back
                          // to - retry with that instead of hiding. If the img now on screen is
                          // already the bundled SVG (or there's nothing left to fall back to), hide
                          // it as before rather than looping.
                          if (!apiIconHasFailed && apiIconUrl && bundledSvgUrl) {
                            setFailedApiIconKeys((prev) => ({ ...prev, [apiIconKey]: true }));
                          } else {
                            event.currentTarget.style.visibility = 'hidden';
                          }
                        }}
                      />
                    )}
                  </span>
                  <span className="app-registry-card-label-newui">
                    <span className="app-registry-card-label-text">{label}</span>
                  </span>
                  <div
                    className={`app-registry-card-container-2${
                      canManageApps ? '' : ' app-registry-card-container-2-hidden'
                    }`}
                  >
                    <div className="app-registry-toggle-newui">
                      <Switch
                        checked={enabled}
                        loading={bulkUpdateOverrides.isPending}
                        onChange={(checked) => handleEnableChange(app, checked)}
                        disabled={!canManageApps || bulkUpdateOverrides.isPending}
                      />
                      <span>{trans('ENABLE')}</span>
                    </div>
                  </div>
                </>
              }
            />
          );
        })
      )}
      {canManageApps && (
        <div className="app-registry-action-button-container">
          <div className="form-button">
            <NewCustomButton
              className="cancel"
              onClick={discardPendingChanges}
              label={trans('CANCEL')}
              icon={<NewCloseIcon className="cancel-icon" />}
              disabled={bulkUpdateOverrides.isPending}
            />
          </div>
          <div className="form-button">
            <NewCustomButton
              type={ButtonTypes.primary}
              label={trans('UPDATE')}
              icon={
                <NewUpdateIcon
                  className={hasPendingChanges ? 'update-icon-enabled' : 'update-icon-disabled'}
                />
              }
              onClick={savePendingChanges}
              disabled={!hasPendingChanges || bulkUpdateOverrides.isPending}
            />
          </div>
        </div>
      )}
    </div>
  );
});

AppRegistryList.displayName = 'AppRegistryList';

AppRegistryList.propTypes = {
  useruuid: PropTypes.string.isRequired,
  launcherPlacement: PropTypes.string.isRequired,
  canManageApps: PropTypes.bool,
};

export default AppRegistryList;
